import type { AppId } from '@/integration';
import connectDe from '../locales/de.json';
import connectEn from '../locales/en.json';
import erpDe from '@erp/locales/de.json';
import erpShellEn from '@erp/connect/locales/en.json';
import agentDe from '@agent/locales/de.json';
import agentEn from '@agent/locales/en.json';
import flowDe from '@flow/locales/de.json';
import flowEn from '@flow/locales/en.json';
import sitesDe from '@sites/locales/de.json';
import sitesEn from '@sites/locales/en.json';
import chatDe from '@chat/locales/de.json';
import chatEn from '@chat/locales/en.json';
import notesDe from '@notes/locales/de.json';
import notesEn from '@notes/locales/en.json';
import wikiDe from '@wiki/locales/de.json';
import wikiEn from '@wiki/locales/en.json';
import writeDe from '@write/locales/de.json';
import writeEn from '@write/locales/en.json';
import dashboardDe from '@dashboard/locales/de.json';
import dashboardEn from '@dashboard/locales/en.json';
import mailDe from '@mail/locales/de.json';
import mailEn from '@mail/locales/en.json';
import calendarDe from '@calendar/locales/de.json';
import calendarEn from '@calendar/locales/en.json';
import calcDe from '@calc/locales/de.json';
import calcEn from '@calc/locales/en.json';
import driveDe from '@drive/locales/de.json';
import driveEn from '@drive/locales/en.json';
import designerDe from '@designer/locales/de.json';
import designerEn from '@designer/locales/en.json';
import adminDe from '@admin/locales/de.json';
import adminEn from '@admin/locales/en.json';
// The libraries' locale files, by path: through the package entries the shell
// would load nx-layout, nx-dashboard and write — and all they import — at startup.
import nxUiDe from '../../../ui/src/locales/de.json';
import nxUiEn from '../../../ui/src/locales/en.json';
import connectPackageDe from '../../../packages/connect/src/locales/de.json';
import connectPackageEn from '../../../packages/connect/src/locales/en.json';
import layoutDe from '../../../packages/layout/src/locales/de.json';
import layoutEn from '../../../packages/layout/src/locales/en.json';
import dashboardPackageDe from '../../../packages/dashboard/src/locales/de.json';
import dashboardPackageEn from '../../../packages/dashboard/src/locales/en.json';
import writePackageDe from '../../../packages/write/src/locales/de.json';
import writePackageEn from '../../../packages/write/src/locales/en.json';

// Who owns which texts (I-2284). Every hosted app gets a table of its own at
// runtime — its own locale plus the shared libraries' texts — so an app never
// sees, and never overrides, another app's texts. The keys stay as they are
// (ERP's `sales.quote.…` has no `erp.` prefix); the table is the namespace.

export type Messages = Record<string, unknown>;

/** A locale source: German always, English where the owner ships it. */
export interface LocaleSource {
  de: Messages;
  en?: Messages;
}

export const SHELL_LOCALES = ['de', 'en'] as const;
export type ShellLocale = (typeof SHELL_LOCALES)[number];

/** A ready table: one message tree per shell locale. */
export type MessageTable = Record<ShellLocale, Messages>;

/**
 * Texts of the shared libraries. They are in every table, the shell's included,
 * because every app renders the kit; nobody else defines these keys.
 */
export const libraryLocales: Readonly<Record<string, LocaleSource>> = {
  '@blpde/nx-ui': { de: nxUiDe, en: nxUiEn },
  '@blpde/nx-layout': { de: layoutDe, en: layoutEn },
  '@blpde/connect': { de: connectPackageDe, en: connectPackageEn },
  '@blpde/nx-dashboard': { de: dashboardPackageDe, en: dashboardPackageEn },
  '@blpde/write': { de: writePackageDe, en: writePackageEn },
};

/** The shell's own texts (`connect.*`, its login form, …). */
export const shellLocale: LocaleSource = { de: connectDe, en: connectEn };

/**
 * Every hosted app's own locale. Flow's and Chat's English tables hold only their
 * command-palette texts so far (I-2348), the rest falls back to German. The ERP is
 * not translated yet: its English table is the shell surface alone — navbar,
 * palette entries, the cockpit captions its tile jumps read, its runtime palette
 * commands — and lives in `src/connect/locales/`, outside the ERP's i18n lint
 * glob, which would demand every German key in English (`erpShellLocale.test.ts`).
 */
export const appLocales: Readonly<Record<AppId, LocaleSource>> = {
  erp: { de: erpDe, en: erpShellEn },
  designer: { de: designerDe, en: designerEn },
  mail: { de: mailDe, en: mailEn },
  calendar: { de: calendarDe, en: calendarEn },
  calc: { de: calcDe, en: calcEn },
  drive: { de: driveDe, en: driveEn },
  agent: { de: agentDe, en: agentEn },
  flow: { de: flowDe, en: flowEn },
  sites: { de: sitesDe, en: sitesEn },
  chat: { de: chatDe, en: chatEn },
  wiki: { de: wikiDe, en: wikiEn },
  notes: { de: notesDe, en: notesEn },
  write: { de: writeDe, en: writeEn },
  dashboard: { de: dashboardDe, en: dashboardEn },
  admin: { de: adminDe, en: adminEn },
};

function isObject(v: unknown): v is Messages {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Merges message trees into a new one. Nothing of a layer is shared with the
 * result: vue-i18n's `mergeLocaleMessage` — which the ERP's definition texts
 * arrive through (BLP/nx#3257) — writes into the table in place, and a subtree
 * still shared with a locale file would change the file's module for every
 * table built after it.
 */
export function deepMerge(...layers: Messages[]): Messages {
  return layers.reduce<Messages>((acc, src) => {
    const out: Messages = { ...acc };
    for (const key of Object.keys(src)) {
      const sv = src[key];
      const tv = out[key];
      if (isObject(sv) && isObject(tv)) out[key] = deepMerge(tv, sv);
      else out[key] = isObject(sv) ? deepMerge(sv) : sv;
    }
    return out;
  }, {});
}

/**
 * A source's texts in one locale. English lies over German, so a missing English
 * text shows the German one right away — vue-i18n's `fallbackLocale: 'de'` would
 * do the same, per lookup and with a warning each time.
 */
function inLocale(source: LocaleSource, locale: ShellLocale): Messages {
  if (locale === 'de' || !source.en) return source.de;
  return deepMerge(source.de, source.en);
}

/**
 * One owner's table: its own texts plus the shared libraries'. The libraries
 * come last, so their texts win even over an app that redefines one — the
 * ownership test (`ownership.test.ts`) turns that red anyway.
 */
export function buildMessageTable(own: LocaleSource): MessageTable {
  const sources = [own, ...Object.values(libraryLocales)];
  return {
    de: deepMerge(...sources.map((source) => inLocale(source, 'de'))),
    en: deepMerge(...sources.map((source) => inLocale(source, 'en'))),
  };
}
